import React, { useEffect, useState } from "react";
import { RiMoneyDollarCircleLine } from "react-icons/ri";
import { FaCheckDouble, FaEdit, FaPlus } from "react-icons/fa";
import { IoMdClose, IoMdTrash } from "react-icons/io";
import { FieldArray, Form, Formik, useFormikContext } from "formik";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import moment from "moment";

import CardHeading from "../../../shared/CardHeading";
import DatePicker from "../../../shared/DatePicker";
import TextInput from "../../../shared/TextInput";
import DropdownField from "../../../shared/DropdownField";
import CustomButton from "../../../shared/CustomButton";
import CustomTable from "../../../shared/CustomTable";
import { insurancePlansColumsData } from "../../../constants/OrderConstants";
import {
  classTypeOptions,
  payersOptionsFormat,
  subscriberRelationOptions,
} from "../../../constants/BeneficiaryConstants";
import { useTranslation } from "react-i18next";
import SwitchInput from "../../../shared/SwitchInput";
import CheckPermission from "../../../hooks/CheckPermission";
const ReusableModal = React.lazy(() =>
  import("../../../shared/ReuseableModal")
);

const getPolicyHolder = (plan) =>
  plan?.policy_holder ||
  plan?.PolicyHolder ||
  plan?.policyHolder ||
  plan?.holder_name ||
  plan?.payer_name ||
  "";

const InsurancePlansForm = () => {
  const { hasPermission } = CheckPermission();
  const { values, setFieldValue } = useFormikContext();
  const { isLoading } = useSelector((state) => state?.visits);
  const [openInsurancePlansModal, setOpenInsurancePlansModal] = useState(false);
  const [selectedPlanData, setSelectedPlanData] = useState(null);
  const [dismissedMissingPayerMessage, setDismissedMissingPayerMessage] =
    useState(false);
  const { t } = useTranslation();
  const { payersData } = useSelector((state) => state?.visits);
  const formattedPayersOptions = payersOptionsFormat(payersData?.data);
  const missingPayerNames = [
    ...new Set(
      (values?.insurance_plans || [])
        .filter((plan) => !plan?.his_payer_id && plan?.missing_payer_name)
        .map((plan) => plan.missing_payer_name)
    ),
  ];
  const missingPayerKey = missingPayerNames.join("|");

  useEffect(() => {
    setDismissedMissingPayerMessage(false);
  }, [missingPayerKey]);

  const primaryColumn = {
    header: t("Is Primary"),
    body: (rowData, column) => (
      <SwitchInput
        value={rowData?.is_primary}
        handleChange={(e) => {
          const isChecked = e.value;
          const updatedPlans = values.insurance_plans.map((plan, index) => ({
            ...plan,
            is_primary: index === column.rowIndex ? isChecked : false,
          }));
          setFieldValue("insurance_plans", updatedPlans);
        }}
      />
    ),
    headerCellClass: "!bg-gray-200",
  };

  const actionsColumn = {
    body: actionsTemplate,
    header: t("Actions"),
    headerCellClass: "!bg-gray-200",
  };

  const payerColumn = {
    header: t("PayerName"),
    headerCellClass: "!bg-gray-200",
    body: (rowData, column) => (
          <DropdownField
        useFormik={false}
        filter
        value={rowData?.his_payer_id || rowData?.payer_id}
        options={formattedPayersOptions}
        handleChange={(e) => {
          const payerId = e.value;
          const selectedPayer = payersData?.data?.find(
            (payer) => payer?.payer_id === payerId
          );
          const payerName = selectedPayer?.name || "";

          const updatedPlans = (values.insurance_plans || []).map(
            (plan, index) => {
              if (index === column.rowIndex) {
                const updatedPlan = {
                  ...plan,
                  payer_id: payerId,
                  his_payer_id: payerId,
                  payer_name: payerName,
                  missing_payer_name: "",
                };

                if (
                  values?.benificiaryType === "corporate" ||
                  values?.benificiaryType === "government"
                ) {
                  if (selectedPayer?.generals?.deductable > 0) {
                    updatedPlan.patient_share = selectedPayer.generals.deductable;
                  }
                  if (selectedPayer?.generals?.discount > 0) {
                    updatedPlan.discount_percentage = selectedPayer.generals.discount;
                  }
                  if (selectedPayer?.generals?.approval_limit > 0) {
                    updatedPlan.max_limit = selectedPayer.generals.approval_limit;
                  }
                }

                return updatedPlan;
              }
              return plan;
            }
          );

          setFieldValue("insurance_plans", updatedPlans);
        }}
        className="w-full"
        appendTo={typeof document !== "undefined" ? document.body : "self"}
      />
    ),
  };

  const columnsData = [
    actionsColumn,
    payerColumn,
    ...insurancePlansColumsData(t).slice(1),
    primaryColumn,
  ];

  function actionsTemplate(rowData, column) {
    return (
      <div className="flex items-center gap-1">
        {hasPermission('patient', 'update') && (
          <FaEdit
            size={17}
            className="text-gray-500 mx-2 cursor-pointer"
            onClick={() => handleEditPlan(rowData, column)}
          />
        )}
        {hasPermission('patient', 'delete') && (
          <FieldArray name="insurance_plans">
            {({ remove }) => (
              <IoMdTrash
                size={17}
                className="text-red-500 cursor-pointer"
                onClick={() => remove(column?.rowIndex)}
              />
            )}
          </FieldArray>
        )}
      </div>
    );
  }
  
  // // Handle Delete plan
  // const handleDeletePlan = (rowData, column) => {
  //   let index = column?.rowIndex;
  //   const updatedInsurancePlansa = [...values?.insurance_plans];
  //   updatedInsurancePlansa.splice(index, 1);
  //   setFieldValue("insurance_plans", updatedInsurancePlansa);
  // };

  const handleEditPlan = (rowData, column) => {
    const planData = rowData;
    if (planData) {
      setSelectedPlanData(planData);
      setOpenInsurancePlansModal(true);
    }
  };

  const handleOpenCreatePlanModal = () => {
    setOpenInsurancePlansModal(true);
  };

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mt-4 md:mt-6 mb-2">
        <CardHeading
          title={t("Payers")}
          Icon={RiMoneyDollarCircleLine}
          iconSize={20}
        />
        {hasPermission('patient', 'create') && (
          <CustomButton
            disabled={isLoading}
            type="button"
            onClick={handleOpenCreatePlanModal}
            label={values?.benificiaryType === "corporate" ? t("AddCorporatePlan") : t("AddNewInsurancePlan")}
            icon={<FaPlus size={12} className="me-2" />}
            buttonType="primary"
          />
        )}
      </div>
      <div className="w-full mt-4 md:mt-6">
        <CustomTable columns={columnsData} data={values?.insurance_plans} />
      </div>
      {missingPayerNames.length > 0 && !dismissedMissingPayerMessage && (
        <div className="mt-2 flex items-start justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <div>
            {missingPayerNames.map((payerName) => (
              <p key={payerName}>
                {t("This payer")} ({payerName}){" "}
                {t("does not exist in our system. Please select an existing payer.")}
              </p>
            ))}
          </div>
          <button
            type="button"
            className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded text-red-500 hover:bg-red-100 hover:text-red-700"
            onClick={() => setDismissedMissingPayerMessage(true)}
            aria-label={t("Close")}
          >
            <IoMdClose size={16} />
          </button>
        </div>
      )}
      <ReusableModal
        isVisible={openInsurancePlansModal}
        position="top"
        title={
          values?.benificiaryType === "corporate"
            ? selectedPlanData
              ? t("UpdateCorporatePlan")
              : t("AddCorporatePlan")
            : selectedPlanData
              ? t("UpdateInsurancePlan")
              : t("AddNewInsurancePlan")
        }
        HeaderIcon={RiMoneyDollarCircleLine}
        onClose={() => {
          setOpenInsurancePlansModal(false);
          setSelectedPlanData(null);
        }}
        className="w-full md:w-[600px] lg:w-[800px] xl:w-[900px]"
      >
        <InsurancePlansFormFields
          setOpenInsurancePlansModal={setOpenInsurancePlansModal}
          selectedPlanData={selectedPlanData}
          setSelectedPlanData={setSelectedPlanData}
          inusurancePlanValues={values}
          setFieldValue={setFieldValue}
          benificiaryType={values?.benificiaryType}
        />
      </ReusableModal>
    </div>
  );
};

export default InsurancePlansForm;

const InsurancePlansFormFields = ({
  setOpenInsurancePlansModal,
  selectedPlanData,
  setSelectedPlanData,
  inusurancePlanValues,
  setFieldValue,
  benificiaryType,
}) => {
  const { payersData } = useSelector((state) => state?.visits);
  const formattedPayersOptions = payersOptionsFormat(payersData?.data);
  const { values } = useFormikContext();
  const { t } = useTranslation();
  const initialValues = {
    member_card_id: selectedPlanData?.member_card_id || "",
    policy_number: selectedPlanData?.policy_number || "",
    expiry_date: selectedPlanData?.expiry_date
      ? moment(selectedPlanData?.expiry_date, [
        "YYYY-MM-DD",
        "DD/MM/YYYY",
        "DD-MM-YYYY",
        "MM-DD-YYYY",
        "MM-DD-YY",
        "DD-MM-YY",
      ]).toDate()
      : "",
    is_primary: selectedPlanData?.is_primary || true,
    payer_id: selectedPlanData?.payer_id || selectedPlanData?.his_payer_id || "",
    his_payer_id: selectedPlanData?.payer_id || selectedPlanData?.his_payer_id || "",
    payer_name: selectedPlanData?.payer_name || selectedPlanData?.name || "",
    relation_with_subscriber:
      selectedPlanData?.relation_with_subscriber || "self",
    coverage_type: selectedPlanData?.coverage_type || "EHCPOL",
    patient_share: selectedPlanData?.patient_share || 0,
    max_limit: selectedPlanData?.max_limit ?? 100000,
    network: selectedPlanData?.network || "",
    issue_date: selectedPlanData?.issue_date
      ? moment(selectedPlanData?.issue_date, [
        "YYYY-MM-DD",
        "DD/MM/YYYY",
        "DD-MM-YYYY",
        "MM-DD-YYYY",
        "MM-DD-YY",
        "DD-MM-YY",
      ]).toDate()
      : "",
    sponsor_no: selectedPlanData?.sponsor_no || "",
    policy_class: selectedPlanData?.policy_class || "",
    policy_holder: getPolicyHolder(selectedPlanData) || "",
    insurance_type: selectedPlanData?.insurance_type || "",
    insurance_status: selectedPlanData?.insurance_status || "",
    insurance_duration: selectedPlanData?.insurance_duration || "",
    class_type: selectedPlanData?.class_type || "plan",
    class_id: selectedPlanData?.class_id || "",
    class_name: selectedPlanData?.class_name || "",
    discount_percentage: selectedPlanData?.discount_percentage || 0,
  };

  const validationSchema = Yup.object({
    member_card_id: benificiaryType === 'corporate'
      ? Yup.string().trim().notRequired()
      : Yup.string().trim().required(t("Member Card ID is required")),
    policy_number:
      benificiaryType == "insurance"
        ? Yup.string().trim().required(t("Policy number is required"))
        : Yup.string().trim().notRequired(),
    expiry_date: benificiaryType === 'corporate'
      ? Yup.date().notRequired()
      : Yup.date()
        .required(t("Expiry date is required"))
        .test(
          "expiryDate",
          t("Expiry date must be after issue date"),
          function (value) {
            const issueDate = this.parent.issue_date
              ? new Date(this.parent.issue_date)
              : null;
            return issueDate && value ? value > issueDate : true;
          }
        ),
    issue_date: benificiaryType === 'corporate'
      ? Yup.string().trim().notRequired()
      : Yup.string().trim().required(t("Issue Date is required")),
    is_primary: benificiaryType === 'corporate'
      ? Yup.boolean().notRequired()
      : Yup.boolean().required(t("Is primary is required")),
    payer_id: Yup.string().trim().required(t("Payer is required")),
    max_limit: benificiaryType === 'corporate'
      ? Yup.number().notRequired()
      : Yup.number().required(t("Max Limit is required")),
    patient_share: benificiaryType === 'corporate'
      ? Yup.number().notRequired().max(100)
      : Yup.number().required(t("Patient Share is required")).max(100),
    relation_with_subscriber: benificiaryType === 'corporate'
      ? Yup.string().trim().notRequired()
      : Yup.string()
        .trim()
        .required(t("Relation with subscriber is required")),
    policy_holder:
      benificiaryType === "insurance"
        ? Yup.string().trim().required(t("Policy holder is required"))
        : Yup.string().trim().notRequired(),
    class_type: benificiaryType === 'corporate'
      ? Yup.string().trim().notRequired()
      : Yup.string().trim().required(t("Class Type is required")),
    // class_name: Yup.string().trim().required('Class Name is required'),
    class_id:
      benificiaryType == "insurance"
        ? Yup.string().trim().required(t("Class ID is required"))
        : Yup.string().trim().notRequired(),
  });

  const handleFormSubmit = (values) => {
    let formatedValues = {
      ...values,
      max_limit: values?.max_limit ?? 0,
      patient_share: values?.patient_share ?? 0,
      issue_date: values?.issue_date ? moment(values?.issue_date).format("DD/MM/YYYY") : "",
      expiry_date: values?.expiry_date ? moment(values?.expiry_date).format("DD/MM/YYYY") : "",
      discount_percentage: Number(values?.discount_percentage) || 0,
      check_eligible: true,
      missing_payer_name: "",
      payer_name:
        payersData?.data?.find((payer) => payer?.payer_id == values?.payer_id)
          ?.name || "",
    };
    formatedValues.policy_holder =
      formatedValues.policy_holder || formatedValues.payer_name || "";

    if (selectedPlanData) {
      const updatedPlans = inusurancePlanValues?.insurance_plans.map(
        (plan, index) =>
          index == selectedPlanData?.index
            ? { ...formatedValues }
            : formatedValues.is_primary
              ? { ...plan, is_primary: false }
              : plan
      );
      setFieldValue("insurance_plans", updatedPlans);
    } else {
      const currentPlans = formatedValues.is_primary
        ? (inusurancePlanValues?.insurance_plans || []).map((plan) => ({
          ...plan,
          is_primary: false,
        }))
        : inusurancePlanValues?.insurance_plans || [];
      setFieldValue("insurance_plans", [...currentPlans, formatedValues]);
    }
    setOpenInsurancePlansModal(false);
    if (setSelectedPlanData) {
      setSelectedPlanData(null);
    }
  };

  return (
    <div>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
      >
        {(props) => (
          <Form>
            <div className="grid grid-cols-2  lg:grid-cols-4 gap-3 mt-4">
              <DropdownField
                filter
                label={t("SelectPayer*")}
                placeholder={t("Selectpayer")}
                options={formattedPayersOptions}
                id="select-payer"
                name="payer_id"
                onChange={(e) => {
                  let value = e?.target?.value;
                  if (values?.benificiaryType == 'corporate' || values?.benificiaryType == 'government') {
                    const selectedPayer = payersData?.data?.find((payer) => payer?.payer_id == value)
                    if (selectedPayer?.generals?.deductable > 0) {
                      props?.setFieldValue("patient_share", selectedPayer.generals.deductable);
                    }
                    if (selectedPayer?.generals?.discount > 0) {
                      props?.setFieldValue("discount_percentage", selectedPayer.generals.discount);
                    }
                    if (selectedPayer?.generals?.approval_limit > 0) {
                      props?.setFieldValue("max_limit", selectedPayer.generals.approval_limit);
                    }
                  }
                  props.setFieldValue("payer_id", value);
                  props?.setFieldValue("his_payer_id", value);
                }}
              />
              {benificiaryType !== 'corporate' && (
                <>
                  <DatePicker
                    useFormik={true}
                    label={t("IssueDate*")}
                    placeholder={t("Selectissuedate")}
                    name="issue_date"
                    dateFormat="dd/mm/yy"
                  />
                  <DatePicker
                    useFormik={true}
                    label={t("ExpiryDate*")}
                    placeholder={t("Selectexpirydate")}
                    name="expiry_date"
                    dateFormat="dd/mm/yy"
                  />
                </>
              )}
              <TextInput
                label={benificiaryType === 'corporate' ? t("MemberCardID") : t("MemberCardID*")}
                placeholder={t("Entermembercardid")}
                name="member_card_id"
              />
              {benificiaryType !== 'corporate' && (
                <TextInput
                  label={t("PolicyNumber*")}
                  placeholder={t("Enterpolicynumber")}
                  name="policy_number"
                />
              )}
              {benificiaryType !== 'corporate' && (
                <DropdownField
                  label={t("RelationwithSubscriber")}
                  placeholder={t("Selectrelation")}
                  name="relation_with_subscriber"
                  options={subscriberRelationOptions}
                />
              )}
              <TextInput
                label={benificiaryType === 'corporate' ? t("PatientShare") : t("PatientShare*")}
                type="number"
                placeholder={t("Addpatientshare")}
                name="patient_share"
              />
              <TextInput
                label={benificiaryType === 'corporate' ? t("MaxLimit") : t("MaxLimit*")}
                placeholder={t("Addmaxlimit")}
                name="max_limit"
              />
              {["corporate", "government"].includes(benificiaryType) && <TextInput
                label={t("Discount") + " " + "%"}
                placeholder={t("Enterdiscountpercentage")}
                type="number"
                min={0}
                max={100}
                step={"any"}
                name="discount_percentage"
              />}
              {benificiaryType !== 'corporate' && (
                <>
                  <TextInput
                    label={t("SponsorNumber")}
                    placeholder={t("Entersponsornumber")}
                    name="sponsor_no"
                  />
                  <TextInput
                    label={t("PolicyClass")}
                    placeholder={t("Enterpolicyclass")}
                    name="policy_class"
                  />
                  <TextInput
                    label={t("PolicyHolder")}
                    placeholder={t("Enterpolicyholder")}
                    name="policy_holder"
                  />
                  <TextInput
                    label={t("InsuranceStatus")}
                    placeholder={t("Enterinsurancestatus")}
                    name="insurance_status"
                  />
                  <TextInput
                    label={t("InsuranceDuration")}
                    placeholder={t("Enterinsuranceduration")}
                    name="insurance_duration"
                  />
                  <TextInput
                    label={t("InsuranceType")}
                    placeholder={t("Enterinsurancetype")}
                    name="insurance_type"
                  />
                  <TextInput
                    label={t("CoverageType")}
                    placeholder={t("Entercoveragetype")}
                    name="coverage_type"
                  />
                  <TextInput
                    label={t("Network")}
                    placeholder={t("Enternetwork")}
                    name="network"
                  />
                  <TextInput
                    label={t("ClassID*")}
                    placeholder={t("Enterclassid")}
                    name="class_id"
                  />
                  <TextInput
                    label={t("ClassName*")}
                    placeholder={t("Enterclassname")}
                    name="class_name"
                  />

                  <DropdownField
                    label={t("ClassType*")}
                    placeholder={t("Selectclasstype")}
                    name="class_type"
                    options={classTypeOptions}
                  />
                </>
              )}
            </div>
            <div className="flex items-center justify-between mt-4">
              <SwitchInput
                label={t("Is Primary")}
                value={props?.values?.is_primary}
                handleChange={(e) => {
                  props?.setFieldValue("is_primary", e.target.value);
                }}
                parentClass="!w-fit !mb-0"
              />
              <CustomButton
                icon={<FaCheckDouble size={12} className="me-2" />}
                type="submit"
                label={selectedPlanData ? t("UpdatePlan") : t("AddNewPlan")}
                buttonType="primary"
              />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};
